import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { url_servicios } from 'src/app/config/config';
import { ChartCommentV2, buildChartCommentV2 } from '../models/v2/chart-comment.v2.model';
import { ListParameters, ListResponse } from '../models';
import { RepositoryV2Service } from './repository.v2.service';

@Injectable({ providedIn: 'root' })
export class ChartCommentsV2Service extends RepositoryV2Service<ChartCommentV2> {
  constructor() {
    super(inject(HttpClient), `${url_servicios}/v2/chart-comments`);
  }

  transform(data: unknown): ChartCommentV2 {
    return buildChartCommentV2(data as Partial<ChartCommentV2>) as ChartCommentV2;
  }

  getChartCommentsByPlanId(
    planId: number,
    bipId?: number,
    dateStart?: string,
    dateEnd?: string,
    perPage: number = 2500
  ): Observable<ListResponse<ChartCommentV2>> {
    const options: ListParameters & { plan_id: number, bip_id?: number} = {
      plan_id: planId,
      per_page: perPage,
      page: 1,
      cache: false,
      search: ''
    };

    if (typeof bipId === 'number') {
      options['bip_id'] = bipId;
    }

    if (dateStart) {
      options['date_start'] = dateStart;
    }

    if (dateEnd) {
      options['date_end'] = dateEnd;
    }

    return this.list(options);
  }

  getChartCommentsByBipId(
    bipId: number,
    dateStart?: string,
    dateEnd?: string,
    perPage: number = 2500
  ): Observable<ListResponse<ChartCommentV2>> {
    const options: ListParameters & { bip_id: number } = {
      bip_id: bipId,
      per_page: perPage,
      page: 1,
      cache: false,
      search: ''
    };

    if (dateStart) {
      options['date_start'] = dateStart;
    }

    if (dateEnd) {
      options['date_end'] = dateEnd;
    }

    return this.list(options);
  }

  getChartCommentsForPdfGeneration(
    bipId: number,
    endDate: Date = new Date(),
    months: number = 6
  ): Observable<{ [planId: number]: ChartCommentV2[] }> {
    const normalizedMonths = Number.isFinite(months) ? Math.max(1, Math.trunc(months)) : 6;

    const end = this.toUtcDate(endDate);

    const startDate = new Date(end);
    startDate.setUTCMonth(startDate.getUTCMonth() - normalizedMonths);

    const dateStart = this.formatDate(startDate);
    const dateEnd = this.formatDate(end);

    return this.getChartCommentsByBipId(bipId, dateStart, dateEnd).pipe(
      map((response) => {
        const grouped: { [planId: number]: ChartCommentV2[] } = {};
        (response.data || []).forEach((comment) => {
          if (comment.plan_id === null) {
            return;
          }

          if (!grouped[comment.plan_id]) {
            grouped[comment.plan_id] = [];
          }
          grouped[comment.plan_id].push(comment);
        });

        Object.keys(grouped).forEach((planId) => {
          grouped[Number(planId)].sort((a, b) => {
            const aDate = new Date(a.comment_date).getTime();
            const bDate = new Date(b.comment_date).getTime();
            return aDate - bDate;
          });
        });

        return grouped;
      })
    );
  }

  private toUtcDate(input: Date): Date {
    const base = input ?? new Date();
    return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate()));
  }

  private formatDate(date: Date): string {
    const normalized = new Date(date);
    const year = normalized.getUTCFullYear();
    const month = String(normalized.getUTCMonth() + 1).padStart(2, '0');
    const day = String(normalized.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
